// ===== 2D lattice math for the Lattices lab =====
//
// A lattice is the set of all integer linear combinations of a basis
// { a*b1 + b*b2 : a, b in Z }. Every lattice has infinitely many bases; the
// "good" ones are short and nearly orthogonal, the "bad" ones are long and
// skewed. Finding a good basis is the hard part, and the gap between what you
// can do with a good basis (the private key) versus a bad one (the public key)
// is the trapdoor behind lattice-based cryptography.
//
// Everything here is exact 2D linear algebra so it can be unit tested and
// rendered live in the browser. The hardness story is about high dimensions;
// 2D is where the geometry is legible.

export type Vec2 = readonly [number, number];

// A basis is an ordered pair of vectors (the columns of the basis matrix B).
export type Basis = readonly [Vec2, Vec2];

// 2x2 integer matrix with determinant +/-1. Applying one to a basis produces a
// different basis for the *same* lattice.
export type Unimodular = readonly [readonly [number, number], readonly [number, number]];

export interface LatticePreset {
  id: string;
  name: string;
  description: string;
  /** Short, nearly orthogonal basis. This is the "private key" / good basis. */
  good: Basis;
  /** Unimodular transform applied to `good` to produce the long, skewed
   * "public key" / bad basis. Same lattice, harder to work with. */
  scramble: Unimodular;
}

export interface LatticePoint {
  x: number;
  y: number;
  /** Integer coordinates in the basis used to generate the point. */
  a: number;
  b: number;
}

export interface ReductionStep {
  basis: Basis;
  /** Human-readable description of the operation that produced this basis. */
  note: string;
}

export interface BabaiResult {
  /** Rounded integer coordinates in the basis that was used. */
  coeffs: [number, number];
  /** The lattice point those coordinates land on. */
  point: Vec2;
}

export interface ClosestResult {
  coeffs: [number, number];
  point: Vec2;
  dist: number;
}

// ===== basic vector ops =====

export function add(u: Vec2, v: Vec2): Vec2 {
  return [u[0] + v[0], u[1] + v[1]];
}

export function sub(u: Vec2, v: Vec2): Vec2 {
  return [u[0] - v[0], u[1] - v[1]];
}

export function scale(u: Vec2, k: number): Vec2 {
  return [u[0] * k, u[1] * k];
}

export function dot(u: Vec2, v: Vec2): number {
  return u[0] * v[0] + u[1] * v[1];
}

export function norm(u: Vec2): number {
  return Math.sqrt(dot(u, u));
}

/** Signed area of the fundamental parallelogram. Its absolute value is the
 * lattice covolume and is invariant under change of basis. */
export function det(basis: Basis): number {
  const [b1, b2] = basis;
  return b1[0] * b2[1] - b1[1] * b2[0];
}

// ===== building a basis from a lattice =====

/** Apply an integer matrix U to a basis B, returning the columns of B * U.
 * When det(U) = +/-1 the result spans the same lattice. */
export function applyUnimodular(basis: Basis, u: Unimodular): Basis {
  const [b1, b2] = basis;
  const c1 = add(scale(b1, u[0][0]), scale(b2, u[1][0]));
  const c2 = add(scale(b1, u[0][1]), scale(b2, u[1][1]));
  return [c1, c2];
}

/** The bad/public basis for a preset: the good basis scrambled. */
export function badBasis(preset: LatticePreset): Basis {
  return applyUnimodular(preset.good, preset.scramble);
}

// ===== lattice point generation =====

/** Solve B * [a, b]^T = target for real a, b. Returns null for a degenerate basis. */
export function solveCoeffs(basis: Basis, target: Vec2): [number, number] | null {
  const d = det(basis);
  if (Math.abs(d) < 1e-12) return null;
  const [b1, b2] = basis;
  // Inverse of [[b1x, b2x], [b1y, b2y]] applied to target.
  const a = (b2[1] * target[0] - b2[0] * target[1]) / d;
  const b = (-b1[1] * target[0] + b1[0] * target[1]) / d;
  return [a, b];
}

/** Generate every lattice point whose screen position falls inside the given
 * world bounds (with a margin), capped at `maxCount` to stay responsive. */
export function generateLatticePoints(
  basis: Basis,
  bounds: { xMin: number; xMax: number; yMin: number; yMax: number },
  maxCount = 4000
): LatticePoint[] {
  const margin = 0.1 * Math.max(bounds.xMax - bounds.xMin, bounds.yMax - bounds.yMin);
  const corners: Vec2[] = [
    [bounds.xMin - margin, bounds.yMin - margin],
    [bounds.xMax + margin, bounds.yMin - margin],
    [bounds.xMin - margin, bounds.yMax + margin],
    [bounds.xMax + margin, bounds.yMax + margin],
  ];

  let aMin = Infinity;
  let aMax = -Infinity;
  let bMin = Infinity;
  let bMax = -Infinity;
  for (const corner of corners) {
    const coeffs = solveCoeffs(basis, corner);
    if (!coeffs) return [];
    aMin = Math.min(aMin, coeffs[0]);
    aMax = Math.max(aMax, coeffs[0]);
    bMin = Math.min(bMin, coeffs[1]);
    bMax = Math.max(bMax, coeffs[1]);
  }

  const aLo = Math.floor(aMin);
  const aHi = Math.ceil(aMax);
  const bLo = Math.floor(bMin);
  const bHi = Math.ceil(bMax);

  // Bail out rather than freeze the tab when the basis is near-degenerate.
  if ((aHi - aLo + 1) * (bHi - bLo + 1) > maxCount * 4) return [];

  const [b1, b2] = basis;
  const points: LatticePoint[] = [];
  for (let a = aLo; a <= aHi; a++) {
    for (let b = bLo; b <= bHi; b++) {
      const x = a * b1[0] + b * b2[0];
      const y = a * b1[1] + b * b2[1];
      if (
        x >= bounds.xMin - margin &&
        x <= bounds.xMax + margin &&
        y >= bounds.yMin - margin &&
        y <= bounds.yMax + margin
      ) {
        points.push({ x, y, a, b });
        if (points.length >= maxCount) return points;
      }
    }
  }
  return points;
}

// ===== Lagrange / Gauss basis reduction (exact SVP in 2D) =====

/** Reduce a 2D basis to its shortest, most orthogonal form using Lagrange's
 * algorithm. Records each step so the UI can animate the transformation.
 * The lattice is unchanged: every step is a unimodular operation. */
export function gaussReduce(basis: Basis): {
  reduced: Basis;
  steps: ReductionStep[];
} {
  let v1: Vec2 = [basis[0][0], basis[0][1]];
  let v2: Vec2 = [basis[1][0], basis[1][1]];
  const steps: ReductionStep[] = [{ basis: [v1, v2], note: 'Start' }];
  const eps = 1e-12;

  if (dot(v1, v1) < eps || dot(v2, v2) < eps || Math.abs(det([v1, v2])) < eps) {
    return { reduced: [v1, v2], steps };
  }

  // Safety cap; Lagrange reduction converges quickly in practice.
  for (let iter = 0; iter < 100; iter++) {
    if (dot(v1, v1) > dot(v2, v2)) {
      [v1, v2] = [v2, v1];
      steps.push({ basis: [v1, v2], note: 'Swap so the shorter vector is first' });
    }
    if (dot(v1, v1) < eps) break;
    const mu = Math.round(dot(v1, v2) / dot(v1, v1));
    if (mu === 0) break;
    v2 = sub(v2, scale(v1, mu));
    steps.push({
      basis: [v1, v2],
      note: `Subtract ${mu} x b1 from b2`,
    });
  }

  // Final orientation: shortest vector first.
  if (dot(v1, v1) > dot(v2, v2)) {
    [v1, v2] = [v2, v1];
    steps.push({ basis: [v1, v2], note: 'Swap so the shortest vector is first' });
  }
  return { reduced: [v1, v2], steps };
}

/** The shortest nonzero vector in the lattice (exact in 2D via reduction). */
export function shortestVector(basis: Basis): Vec2 {
  return gaussReduce(basis).reduced[0];
}

/** Orthogonality defect: (|b1| |b2|) / |det B|. Equals 1 for an orthogonal
 * basis and grows as the basis gets more skewed. A compact "how bad is this
 * basis" number. */
export function orthogonalityDefect(basis: Basis): number {
  const d = Math.abs(det(basis));
  if (d < 1e-12) return Infinity;
  return (norm(basis[0]) * norm(basis[1])) / d;
}

/** Gram-Schmidt orthogonalized lengths. The second value bounds how much error
 * Babai rounding can correct, which is why a good basis decrypts and a bad one
 * does not. */
export function gramSchmidtLengths(basis: Basis): [number, number] {
  const [b1, b2] = basis;
  const len1 = norm(b1);
  if (len1 < 1e-12) return [0, norm(b2)];
  const mu = dot(b1, b2) / dot(b1, b1);
  const b2star = sub(b2, scale(b1, mu));
  return [len1, norm(b2star)];
}

// ===== Closest Vector Problem =====

/** Babai's rounding: express the target in the given basis and round each
 * coordinate to the nearest integer. With a good (near-orthogonal) basis this
 * lands on the closest lattice point; with a bad basis it can miss badly. */
export function babaiRound(basis: Basis, target: Vec2): BabaiResult {
  const coeffs = solveCoeffs(basis, target);
  if (!coeffs) return { coeffs: [0, 0], point: [0, 0] };
  const a = Math.round(coeffs[0]);
  const b = Math.round(coeffs[1]);
  const [b1, b2] = basis;
  return {
    coeffs: [a, b],
    point: [a * b1[0] + b * b2[0], a * b1[1] + b * b2[1]],
  };
}

/** True closest lattice point, found by brute-force search around the rounded
 * coordinates. Exact for a reasonable search window; used as ground truth. */
export function closestVector(basis: Basis, target: Vec2, window = 4): ClosestResult {
  const coeffs = solveCoeffs(basis, target);
  const ca = coeffs ? Math.round(coeffs[0]) : 0;
  const cb = coeffs ? Math.round(coeffs[1]) : 0;
  const [b1, b2] = basis;
  let best: ClosestResult = { coeffs: [ca, cb], point: [0, 0], dist: Infinity };
  for (let a = ca - window; a <= ca + window; a++) {
    for (let b = cb - window; b <= cb + window; b++) {
      const point: Vec2 = [a * b1[0] + b * b2[0], a * b1[1] + b * b2[1]];
      const dist = norm(sub(point, target));
      if (dist < best.dist) best = { coeffs: [a, b], point, dist };
    }
  }
  return best;
}

// ===== GGH-style encryption (pedagogical, not production) =====

/** Encode an integer message as a lattice point using the public basis, then
 * add a small error vector. This is the lattice analog of textbook RSA: the
 * ciphertext is a noisy lattice point, and decrypting means solving CVP. */
export function gghEncrypt(publicBasis: Basis, message: [number, number], error: Vec2) {
  const [b1, b2] = publicBasis;
  const point: Vec2 = [
    message[0] * b1[0] + message[1] * b2[0],
    message[0] * b1[1] + message[1] * b2[1],
  ];
  return { point, ciphertext: add(point, error) };
}

/** Decrypt by solving CVP with the chosen basis, then reading the recovered
 * lattice point back out as a message in the public basis. Pass the good
 * (private) basis to succeed; pass the bad (public) basis to see it fail. */
export function gghDecrypt(
  decryptBasis: Basis,
  publicBasis: Basis,
  ciphertext: Vec2
): { point: Vec2; message: [number, number] } {
  const point = babaiRound(decryptBasis, ciphertext).point;
  const coeffs = solveCoeffs(publicBasis, point);
  const message: [number, number] = coeffs
    ? [Math.round(coeffs[0]), Math.round(coeffs[1])]
    : [0, 0];
  return { point, message };
}

// ===== presets =====

export const LATTICE_PRESETS: LatticePreset[] = [
  {
    id: 'square',
    name: 'Square',
    description: 'An almost-square lattice. Easiest case for building intuition.',
    good: [
      [1.6, 0],
      [0, 1.6],
    ],
    scramble: [
      [2, 1],
      [1, 1],
    ],
  },
  {
    id: 'hex',
    name: 'Hexagonal',
    description: 'The densest 2D packing. Basis vectors meet at 60 degrees.',
    good: [
      [1.7, 0],
      [0.85, 1.472],
    ],
    scramble: [
      [3, 2],
      [4, 3],
    ],
  },
  {
    id: 'wide',
    name: 'Anisotropic',
    description: 'Stretched in x. Shows how rounding fails along the long axis.',
    good: [
      [2.4, 0],
      [0, 1.0],
    ],
    scramble: [
      [1, 1],
      [-2, -1],
    ],
  },
];

export function presetById(id: string): LatticePreset | undefined {
  return LATTICE_PRESETS.find((p) => p.id === id);
}
